import { useState } from 'react';

const milestones = [
  { label: 'First Solo', date: 'Feb 14, 2025', score: 81, done: true },
  { label: 'Solo Cross-Country', date: 'Mar 22, 2025', score: 87, done: true },
  { label: 'Night Currency', date: 'Apr 5, 2025', score: 90, done: true },
  { label: 'Instrument Intro', date: 'Apr 18, 2025', score: null, done: false },
  { label: 'Pre-Solo Check', date: 'May 2, 2025', score: null, done: false },
];

const weeklyData = [
  { week: 'W1', flights: 2, avgScore: 74, hours: 2.4 },
  { week: 'W2', flights: 3, avgScore: 79, hours: 3.8 },
  { week: 'W3', flights: 2, avgScore: 83, hours: 2.6 },
  { week: 'W4', flights: 4, avgScore: 87, hours: 5.1 },
  { week: 'W5', flights: 3, avgScore: 89, hours: 3.9 },
  { week: 'W6', flights: 3, avgScore: 92, hours: 4.2 },
];

const skills = [
  { name: 'Takeoffs & Landings', level: 88, prev: 80, color: '#00FFD1' },
  { name: 'Radio Communication', level: 94, prev: 85, color: '#00A3E0' },
  { name: 'Traffic Pattern', level: 85, prev: 76, color: '#B4FF39' },
  { name: 'Steep Turns', level: 79, prev: 68, color: '#00FFD1' },
  { name: 'Slow Flight', level: 91, prev: 84, color: '#00A3E0' },
  { name: 'Emergency Procedures', level: 76, prev: 65, color: '#B4FF39' },
];

// Radar chart data
const radarSkills = [
  { label: 'Landings', value: 88 },
  { label: 'Radio', value: 94 },
  { label: 'Pattern', value: 85 },
  { label: 'Steep Turns', value: 79 },
  { label: 'Slow Flight', value: 91 },
  { label: 'Emergency', value: 76 },
];

function RadarChart() {
  const cx = 130, cy = 130, r = 100;
  const n = radarSkills.length;
  const angles = radarSkills.map((_, i) => (i / n) * 2 * Math.PI - Math.PI / 2);

  const rings = [0.25, 0.5, 0.75, 1.0];

  const toXY = (angle: number, radius: number) => ({
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  });

  const dataPoints = radarSkills.map((s, i) => toXY(angles[i], (s.value / 100) * r));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';

  return (
    <svg viewBox="0 0 260 260" className="w-full h-full">
      {/* Grid rings */}
      {rings.map((ring, ri) => {
        const pts = angles.map((a) => toXY(a, ring * r));
        const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
        return (
          <g key={ri}>
            <path d={path} fill="none" stroke="#1A2942" strokeWidth="1" />
            <text x={cx + 4} y={cy - ring * r + 4} fill="#374151" fontSize="8" fontFamily="monospace">
              {Math.round(ring * 100)}
            </text>
          </g>
        );
      })}

      {/* Spokes */}
      {angles.map((a, i) => {
        const end = toXY(a, r);
        return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#1A2942" strokeWidth="1" />;
      })}

      {/* Data area */}
      <path d={dataPath} fill="#00FFD1" fillOpacity="0.12" stroke="#00FFD1" strokeWidth="2" strokeLinejoin="round" />

      {/* Data points */}
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="#00FFD1" stroke="#08121F" strokeWidth="1.5" />
      ))}

      {/* Labels */}
      {radarSkills.map((s, i) => {
        const labelPt = toXY(angles[i], r + 18);
        return (
          <text
            key={i}
            x={labelPt.x}
            y={labelPt.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#9CA3AF"
            fontSize="9"
            fontFamily="sans-serif"
            fontWeight="600"
          >
            {s.label}
          </text>
        );
      })}

      {/* Center score */}
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#ffffff" fontSize="22" fontWeight="bold" fontFamily="monospace">85.5</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#9CA3AF" fontSize="9" fontFamily="sans-serif">AVG SKILL</text>
    </svg>
  );
}

export default function FeatureProgressTracking() {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const maxBarH = 100;

  return (
    <section id="progress-tracking" className="py-16 sm:py-24 bg-[#08121F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00A3E0]/10 border border-[#00A3E0]/30 rounded-2xl flex-shrink-0">
            <i className="ri-line-chart-line text-2xl sm:text-3xl text-[#00A3E0]"></i>
          </div>
          <div>
            <div className="text-xs text-[#00A3E0] uppercase tracking-widest font-semibold mb-2">Capability 05</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Progress Tracking Across Flights</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl leading-relaxed">
              See your entire training journey in one place. WingmanFix builds a living record of your skill development — flight by flight, week by week — so you always know exactly where you stand.
            </p>
          </div>
        </div>

        {/* Top row: Radar + Skill Bars */}
        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-6 sm:gap-8 mb-6 sm:mb-8">

          {/* Radar Chart */}
          <div className="lg:col-span-2 bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-[#00A3E0]/20 shadow-xl flex flex-col">
            <h3 className="text-sm sm:text-base font-semibold text-white uppercase tracking-wider mb-3 sm:mb-4">Skill Radar</h3>
            <div className="flex-1 flex items-center justify-center" style={{ minHeight: 240 }}>
              <RadarChart />
            </div>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-800 grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Flights</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">17</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Hours Logged</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#00A3E0]">24.3</p>
              </div>
            </div>
          </div>

          {/* Skill Bars with delta */}
          <div className="lg:col-span-3 bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-700 shadow-xl">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-sm sm:text-base font-semibold text-white uppercase tracking-wider">Skill Breakdown</h3>
              <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-1.5 rounded-full bg-gray-600"></div>
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-1.5 rounded-full bg-[#00FFD1]"></div>
                  <span className="hidden sm:inline">Current</span>
                  <span className="sm:hidden">Now</span>
                </div>
              </div>
            </div>
            <div className="space-y-4 sm:space-y-5">
              {skills.map((s, i) => {
                const delta = s.level - s.prev;
                return (
                  <div key={i}>
                    <div className="flex justify-between mb-2">
                      <span className="text-xs sm:text-sm text-gray-300 truncate pr-2">{s.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs font-semibold text-[#B4FF39]">+{delta}</span>
                        <span className="text-sm font-bold" style={{ color: s.color }}>{s.level}</span>
                      </div>
                    </div>
                    {/* Previous bar */}
                    <div className="h-1.5 bg-[#1A2942] rounded-full overflow-hidden mb-1">
                      <div
                        className="h-full rounded-full bg-gray-600 transition-all duration-700"
                        style={{ width: `${s.prev}%` }}
                      ></div>
                    </div>
                    {/* Current bar */}
                    <div className="h-2.5 bg-[#1A2942] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 relative"
                        style={{ width: `${s.level}%`, backgroundColor: s.color, boxShadow: `0 0 8px ${s.color}60` }}
                      >
                        <div className="absolute right-0 top-0 bottom-0 w-1 rounded-full bg-white/40"></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary stats */}
            <div className="mt-5 sm:mt-6 pt-4 sm:pt-5 border-t border-gray-800 grid grid-cols-3 gap-3 sm:gap-4">
              {[
                { label: 'Avg Improvement', value: '+11 pts', color: '#B4FF39' },
                { label: 'Best Skill', value: 'Radio', color: '#00A3E0' },
                { label: 'Focus Area', value: 'Emergency', color: '#F59E0B' },
              ].map((s, i) => (
                <div key={i} className="text-center bg-[#0A1628] rounded-xl p-2 sm:p-3 border border-gray-800">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 truncate">{s.label}</p>
                  <p className="text-sm sm:text-base font-bold truncate" style={{ color: s.color }}>{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom row: Weekly Chart + Milestones */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">

          {/* Weekly Activity Chart — grouped bars */}
          <div className="bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-700 shadow-xl overflow-x-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-sm sm:text-base font-semibold text-white uppercase tracking-wider">Weekly Activity</h3>
              <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-[#00A3E0]"></div>
                  <span className="hidden sm:inline">Avg Score</span>
                  <span className="sm:hidden">Score</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-[#00FFD1]/40"></div>
                  <span>Hours</span>
                </div>
              </div>
            </div>

            <div className="flex items-end gap-3 sm:gap-4 min-w-[400px]" style={{ height: maxBarH + 40 }}>
              {weeklyData.map((w, i) => {
                const scoreH = (w.avgScore / 100) * maxBarH;
                const hoursH = (w.hours / 6) * maxBarH;
                const isHovered = hoveredBar === i;
                return (
                  <div
                    key={i}
                    className="flex-1 flex flex-col items-center gap-1 cursor-pointer"
                    onMouseEnter={() => setHoveredBar(i)}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    {isHovered && (
                      <div className="text-center mb-1">
                        <p className="text-xs font-bold text-[#00A3E0]">{w.avgScore}</p>
                        <p className="text-xs text-[#00FFD1]">{w.hours}h</p>
                      </div>
                    )}
                    <div className="w-full flex items-end gap-0.5" style={{ height: maxBarH }}>
                      {/* Score bar */}
                      <div
                        className="flex-1 rounded-t-md transition-all duration-300"
                        style={{
                          height: scoreH,
                          backgroundColor: '#00A3E0',
                          opacity: isHovered ? 1 : 0.7,
                          boxShadow: isHovered ? '0 0 10px #00A3E080' : 'none',
                        }}
                      ></div>
                      {/* Hours bar */}
                      <div
                        className="flex-1 rounded-t-md transition-all duration-300"
                        style={{
                          height: hoursH,
                          backgroundColor: '#00FFD1',
                          opacity: isHovered ? 0.7 : 0.35,
                        }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{w.week}</p>
                    {/* Flight dots */}
                    <div className="flex gap-0.5">
                      {Array.from({ length: w.flights }).map((_, j) => (
                        <div key={j} className="w-1.5 h-1.5 rounded-full bg-[#B4FF39]"></div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-gray-500">
              <span><span className="text-[#B4FF39] font-bold">●</span> Dots = flights flown that week</span>
              <span>6-week rolling window</span>
            </div>
          </div>

          {/* Milestone Timeline */}
          <div className="bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-700 shadow-xl">
            <h3 className="text-sm sm:text-base font-semibold text-white uppercase tracking-wider mb-4 sm:mb-6">Training Milestones</h3>
            <div className="relative">
              <div className="absolute left-4 sm:left-5 top-0 bottom-0 w-px bg-gray-700"></div>
              <div className="space-y-4 sm:space-y-5">
                {milestones.map((m, i) => (
                  <div key={i} className="flex items-start gap-4 sm:gap-5 relative">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 ${
                      m.done
                        ? 'bg-[#00FFD1]/20 border-[#00FFD1]'
                        : 'bg-[#1A2942] border-gray-600'
                    }`}>
                      {m.done
                        ? <i className="ri-checkbox-circle-fill text-[#00FFD1] text-base sm:text-lg"></i>
                        : <i className="ri-time-line text-gray-500 text-base sm:text-lg"></i>
                      }
                    </div>
                    <div className="flex-1 pt-1 bg-[#0A1628] rounded-xl px-3 sm:px-4 py-2 sm:py-3 border border-gray-800">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs sm:text-sm font-semibold truncate ${m.done ? 'text-white' : 'text-gray-500'}`}>{m.label}</p>
                        {m.score && (
                          <span className="text-xs sm:text-sm font-bold text-[#00FFD1] bg-[#00FFD1]/10 px-2 py-0.5 rounded-full whitespace-nowrap">{m.score}</span>
                        )}
                        {!m.done && (
                          <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full whitespace-nowrap">Upcoming</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{m.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pre-Solo Readiness */}
            <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-800">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Pre-Solo Readiness</span>
                <span className="text-base sm:text-lg font-bold text-[#B4FF39]">87%</span>
              </div>
              <div className="h-3 sm:h-4 bg-[#1A2942] rounded-full overflow-hidden relative">
                <div
                  className="h-full rounded-full relative"
                  style={{ width: '87%', background: 'linear-gradient(90deg, #B4FF39, #00FFD1)', boxShadow: '0 0 12px #B4FF3960' }}
                >
                  <div className="absolute right-0 top-0 bottom-0 w-1 sm:w-1.5 rounded-full bg-white/50"></div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-between gap-1 sm:gap-0 mt-2">
                <p className="text-xs text-gray-500">Based on ACS standards across all tracked maneuvers</p>
                <p className="text-xs text-[#B4FF39] font-semibold whitespace-nowrap">13% to go</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}